import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { mocApi } from '../api/client';
import { Skeleton } from './Validations';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Lock, UsersThree, ShieldCheck, Stack, PencilSimple, UserPlus, IdentificationCard, BellRinging, MapPin, Plus, HardHat } from '@phosphor-icons/react';

const ROLES_STATIC = [
  ['ROLE-01', 'Initiateur'], ['ROLE-02', 'N+1 Initiateur'], ['ROLE-03', 'Pilote MOC'],
  ['ROLE-04', 'Coordinateur MOC'], ['ROLE-05', 'Propriétaire Système'], ['ROLE-06', 'Responsable SST'],
  ['ROLE-07', 'Membre Comité MOC'], ['ROLE-08', 'Directeur Site'], ['ROLE-09', 'Membre PSSR'],
  ['ROLE-10', 'Responsable Action'], ['ROLE-11', 'Administrateur Site'], ['ROLE-12', 'Administrateur Général'],
];
const MOC_TYPES = [
  ['TYPE-01', 'Structure / Équipements'], ['TYPE-02', 'Paramètres process / Instrumentation'],
  ['TYPE-03', 'Matières / Fluides'], ['TYPE-04', 'Produits'], ['TYPE-05', 'Organisation'],
];

export default function Admin() {
  const [myRoles, setMyRoles] = useState(null);
  const [users, setUsers] = useState([]);
  const [roleList, setRoleList] = useState(ROLES_STATIC);
  const [sites, setSites] = useState([]);
  const [edit, setEdit] = useState(null);       // user being created/edited
  const [rolesFor, setRolesFor] = useState(null); // user whose roles are edited
  const [allSites, setAllSites] = useState([]);   // every site (active + inactive) for the Sites tab
  const [siteEdit, setSiteEdit] = useState(null); // site being created/edited
  const isAdmin = (myRoles || []).some((r) => ['ROLE-11', 'ROLE-12'].includes(r.Role_ID || r.role_id));

  const loadUsers = useCallback(() => { mocApi.listUsers().then((u) => setUsers(Array.isArray(u) ? u : [])); }, []);
  const loadSites = useCallback(() => {
    mocApi.listAllSites().then((s) => {
      const arr = Array.isArray(s) ? s : [];
      setAllSites(arr);
      if (arr.length) setSites(arr.filter((x) => x.Actif !== false));
    });
  }, []);
  const toggleSite = async (s) => {
    const res = await mocApi.updateSite({ Site_ID: s.Site_ID, Actif: s.Actif === false });
    if (res.ok) { toast.success(s.Actif === false ? 'Site activé' : 'Site désactivé'); loadSites(); }
    else toast.error(res.error || 'Échec');
  };

  useEffect(() => {
    mocApi.getUserRoles().then((r) => {
      const arr = Array.isArray(r) ? r : [];
      setMyRoles(arr);
      if (arr.some((x) => ['ROLE-11', 'ROLE-12'].includes(x.Role_ID))) {
        loadUsers();
        loadSites();
        mocApi.listRoles().then((rl) => { if (rl) setRoleList(rl.map((x) => [x.Role_ID, x.Nom_Role])); });
        (async () => {
          const nat = arr.some((x) => (x.Role_ID || x.role_id) === 'ROLE-12');
          let s = nat ? await mocApi.listSites() : null;
          if (!s || !s.length) { const u = await mocApi.getUserSites(); s = Array.isArray(u) ? u : []; }
          setSites(s || []);
        })();
      }
    });
  }, [loadUsers, loadSites]);

  if (myRoles === null) return <Skeleton />;
  if (!isAdmin) {
    return (
      <Card className="rounded-sm p-8 text-center" data-testid="admin-restricted">
        <Lock size={36} className="mx-auto text-muted-foreground mb-3" />
        <h2 className="font-display font-bold text-lg">Accès réservé</h2>
        <p className="text-sm text-muted-foreground mt-1">Cette page est réservée aux administrateurs (Administrateur Site / Général).</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Administration</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">Utilisateurs & référentiels</h1>
        </div>
        <Button data-testid="admin-new-user" onClick={() => setEdit({ _new: true, Nom: '', Prenom: '', Email: '', Site_Principal_ID: sites[0]?.Site_ID || '' })}>
          <UserPlus size={16} weight="bold" className="mr-1.5" />Nouvel utilisateur
        </Button>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 border-b border-border rounded-none w-full justify-start">
          {[['users', 'Utilisateurs', UsersThree], ['sites', 'Sites', MapPin], ['roles', 'Rôles', ShieldCheck], ['types', 'Types de MOC', Stack], ['sid', 'Safety In Design', HardHat], ['reminders', 'Rappels', BellRinging]].map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-sm gap-1.5">
              <Icon size={16} weight="bold" />{l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="users" className="pt-4">
          <Card className="rounded-sm p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>{['Nom', 'Prénom', 'E-mail', 'Site principal', 'Rôles', 'Statut', ''].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u, i) => (
                  <tr key={u.Utilisateur_ID || i} data-testid={`admin-user-${i}`}>
                    <td className="px-4 py-3 font-medium">{u.Nom || '—'}</td>
                    <td className="px-4 py-3">{u.Prenom || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.Email || u.Utilisateur_ID}</td>
                    <td className="px-4 py-3">{u.Site_Principal_ID || '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{(u.Roles || []).length ? `${u.Roles.length} rôle(s)` : '—'}</td>
                    <td className="px-4 py-3">{u.Actif === false ? <span className="text-xs text-muted-foreground">Inactif</span> : <span className="text-xs text-emerald-600 font-semibold">Actif</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`admin-edit-${i}`} onClick={() => setEdit({ ...u })} className="p-1.5 text-muted-foreground hover:text-primary" title="Modifier"><PencilSimple size={17} /></button>
                      <button data-testid={`admin-roles-${i}`} onClick={() => setRolesFor({ ...u, _roles: (u.Roles || []).map((r) => r.Role_ID), _site: (u.Roles || [])[0]?.Site_ID || u.Site_Principal_ID || sites[0]?.Site_ID || '' })} className="p-1.5 text-muted-foreground hover:text-primary" title="Rôles"><IdentificationCard size={18} /></button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground text-sm">Aucun utilisateur.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="sites" className="pt-4">
          <div className="flex justify-end mb-3">
            <Button data-testid="admin-new-site" size="sm" onClick={() => setSiteEdit({ _new: true, Nom_Site: '', Code_Site: '' })}>
              <Plus size={15} weight="bold" className="mr-1.5" />Nouveau site
            </Button>
          </div>
          <Card className="rounded-sm p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>{['Nom du site', 'Code', 'Statut', ''].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {allSites.map((s, i) => (
                  <tr key={s.Site_ID || i} data-testid={`admin-site-${i}`}>
                    <td className="px-4 py-3 font-medium">{s.Nom_Site}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.Code_Site || '—'}</td>
                    <td className="px-4 py-3">{s.Actif === false ? <span className="text-xs text-muted-foreground">Inactif</span> : <span className="text-xs text-emerald-600 font-semibold">Actif</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`admin-site-edit-${i}`} onClick={() => setSiteEdit({ ...s })} className="p-1.5 text-muted-foreground hover:text-primary" title="Modifier"><PencilSimple size={17} /></button>
                      <button data-testid={`admin-site-toggle-${i}`} onClick={() => toggleSite(s)} className="text-xs px-2 py-1 ml-1 rounded border border-border text-muted-foreground hover:text-foreground">{s.Actif === false ? 'Activer' : 'Désactiver'}</button>
                    </td>
                  </tr>
                ))}
                {allSites.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">Aucun site chargé. Si vous venez d'ajouter le script GAS « Gestion des sites », cliquez sur « Nouveau site ». Sinon, déployez-le d'abord.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="pt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {roleList.map(([id, label]) => (
              <Card key={id} className="rounded-sm p-3 flex items-center gap-3" data-testid={`admin-role-${id}`}>
                <span className="font-mono text-xs text-muted-foreground">{id}</span>
                <span className="text-sm font-medium">{label}</span>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="types" className="pt-4">
          <div className="space-y-2">
            {MOC_TYPES.map(([id, label]) => (
              <Card key={id} className="rounded-sm p-3 flex items-center gap-3" data-testid={`admin-type-${id}`}>
                <span className="font-mono text-xs text-muted-foreground">{id}</span>
                <span className="text-sm font-medium">{label}</span>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="sid" className="pt-4">
          <SidConfig />
        </TabsContent>
        <TabsContent value="reminders" className="pt-4">
          <ReminderSettings />
        </TabsContent>
      </Tabs>

      {edit && <UserDialog user={edit} sites={sites} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); loadUsers(); }} />}
      {rolesFor && <RolesDialog user={rolesFor} roleList={roleList} sites={sites} onClose={() => setRolesFor(null)} onSaved={() => { setRolesFor(null); loadUsers(); }} />}
      {siteEdit && <SiteDialog site={siteEdit} onClose={() => setSiteEdit(null)} onSaved={() => { setSiteEdit(null); loadSites(); }} />}
    </div>
  );
}

function SidConfig() {
  const [tpl, setTpl] = useState(null);
  const [enabled, setEnabled] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    mocApi.getSidTemplate().then((t) => { if (t) { setTpl(t); setEnabled(t.enabled_sections || []); } });
  }, []);
  if (!tpl) return <Skeleton />;
  const toggle = (id) => setEnabled((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setBusy(true);
    const res = await mocApi.updateSidConfig(enabled);
    setBusy(false);
    if (res.ok) toast.success('Sections mises à jour'); else toast.error(res.error || 'Échec');
  };
  return (
    <Card className="rounded-sm p-5 space-y-4 max-w-2xl" data-testid="sid-config">
      <div className="flex items-start gap-3">
        <HardHat size={22} weight="bold" className="text-primary mt-0.5" />
        <div>
          <h3 className="font-display font-bold">Safety In Design — sections actives</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Activez uniquement les sections utiles à vos installations. Elles apparaîtront dans l'onglet « Safety In Design » de chaque MOC.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {tpl.all_sections.map((s) => (
          <label key={s.id} data-testid={`sid-config-${s.id}`} className="flex items-center gap-2.5 text-sm border border-border rounded-sm px-3 py-2 cursor-pointer hover:bg-muted/40">
            <input type="checkbox" className="accent-primary h-4 w-4" checked={enabled.includes(s.id)} onChange={() => toggle(s.id)} />
            <span className="flex-1">{s.title}</span>
            <span className="text-xs text-muted-foreground">{s.count}</span>
          </label>
        ))}
      </div>
      <Button data-testid="sid-config-save" disabled={busy} onClick={save}>Enregistrer</Button>
    </Card>
  );
}

function ReminderSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    mocApi.getReminderSettings().then((s) => setF(s || { lead_days: 3, send_hour: 7, notify_pilote_cc: true }));
  }, []);
  if (!f) return <Skeleton />;
  const save = async () => {
    setBusy(true);
    const res = await mocApi.updateReminderSettings({
      lead_days: Number(f.lead_days) || 0,
      send_hour: Number(f.send_hour) || 0,
      notify_pilote_cc: !!f.notify_pilote_cc,
    });
    setBusy(false);
    if (res.ok) { setF(res.data); toast.success('Paramètres de rappel enregistrés'); }
    else toast.error(res.error || 'Échec');
  };
  return (
    <Card className="rounded-sm p-5 max-w-lg space-y-5" data-testid="reminder-settings">
      <div className="flex items-start gap-3">
        <BellRinging size={22} weight="bold" className="text-primary mt-0.5" />
        <div>
          <h3 className="font-display font-bold">Rappels automatiques</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            E-mail envoyé aux responsables d'actions bloquantes non réalisées avant démarrage.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Délai de rappel (jours avant l'échéance)</label>
        <Input data-testid="reminder-lead-days" type="number" min={0} max={60}
          value={f.lead_days}
          onChange={(e) => setF((s) => ({ ...s, lead_days: e.target.value }))}
          className="h-9 w-32" />
        <p className="text-xs text-muted-foreground">Ex. « 3 » = rappel à J-3 de l'échéance (les actions déjà en retard sont toujours incluses).</p>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Heure d'envoi (UTC)</label>
        <Select value={String(f.send_hour)} onValueChange={(v) => setF((s) => ({ ...s, send_hour: v }))}>
          <SelectTrigger data-testid="reminder-send-hour" className="h-9 w-32"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">
            {Array.from({ length: 24 }).map((_, h) => (
              <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}:00</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Heure universelle (UTC). Le rappel part une fois par jour à cette heure.</p>
      </div>

      <label className="flex items-center gap-2.5 text-sm">
        <input type="checkbox" data-testid="reminder-cc-pilote" className="accent-primary h-4 w-4"
          checked={!!f.notify_pilote_cc}
          onChange={(e) => setF((s) => ({ ...s, notify_pilote_cc: e.target.checked }))} />
        Prévenir aussi le Pilote MOC (en copie)
      </label>

      <Button data-testid="reminder-save" disabled={busy} onClick={save}>Enregistrer</Button>
    </Card>
  );
}

function SiteDialog({ site, onClose, onSaved }) {
  const isNew = site._new;
  const [f, setF] = useState({ Nom_Site: site.Nom_Site || '', Code_Site: site.Code_Site || '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (isNew && !f.Nom_Site.trim()) { toast.error('Nom du site obligatoire'); return; }
    setBusy(true);
    const res = isNew
      ? await mocApi.createSite({ Nom_Site: f.Nom_Site.trim(), Code_Site: f.Code_Site.trim() })
      : await mocApi.updateSite({ Site_ID: site.Site_ID, Code_Site: f.Code_Site.trim() });
    setBusy(false);
    if (res.ok) { toast.success(isNew ? 'Site créé' : 'Site mis à jour'); onSaved(); }
    else toast.error(res.error || 'Échec');
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="site-dialog">
        <DialogHeader><DialogTitle>{isNew ? 'Nouveau site' : 'Modifier le site'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Nom du site</label>
            <Input data-testid="site-name" value={f.Nom_Site} disabled={!isNew}
              onChange={(e) => setF((s) => ({ ...s, Nom_Site: e.target.value }))}
              className="h-9 mt-1" placeholder="Ex. Saint Pierre La Cour" />
            {!isNew && <p className="text-xs text-muted-foreground mt-1">Le nom sert d'identifiant et ne peut pas être modifié.</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Code court</label>
            <Input data-testid="site-code" value={f.Code_Site}
              onChange={(e) => setF((s) => ({ ...s, Code_Site: e.target.value }))}
              className="h-9 mt-1" placeholder="Ex. SPL" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="site-save" disabled={busy} onClick={save}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ user, sites, onClose, onSaved }) {
  const isNew = !!user._new;
  const [f, setF] = useState({ Nom: user.Nom || '', Prenom: user.Prenom || '', Email: user.Email || user.Utilisateur_ID || '', Site_Principal_ID: user.Site_Principal_ID || '', Actif: user.Actif !== false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (isNew && !f.Email.trim()) { toast.error('E-mail obligatoire'); return; }
    setBusy(true);
    const res = isNew
      ? await mocApi.createUser({ Nom: f.Nom, Prenom: f.Prenom, Email: f.Email.trim(), Site_Principal_ID: f.Site_Principal_ID })
      : await mocApi.updateUser({ Utilisateur_ID: user.Utilisateur_ID || user.Email, Nom: f.Nom, Prenom: f.Prenom, Site_Principal_ID: f.Site_Principal_ID, Actif: f.Actif });
    setBusy(false);
    if (res.ok) { toast.success(isNew ? 'Utilisateur créé' : 'Utilisateur mis à jour'); onSaved(); }
    else toast.error(res.error || 'Échec');
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-user-dialog">
        <DialogHeader><DialogTitle className="text-base">{isNew ? 'Nouvel utilisateur' : 'Modifier l\'utilisateur'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-muted-foreground">Nom</label><Input data-testid="user-nom" value={f.Nom} onChange={(e) => setF((s) => ({ ...s, Nom: e.target.value }))} className="h-9 mt-1" /></div>
            <div><label className="text-xs font-semibold text-muted-foreground">Prénom</label><Input data-testid="user-prenom" value={f.Prenom} onChange={(e) => setF((s) => ({ ...s, Prenom: e.target.value }))} className="h-9 mt-1" /></div>
          </div>
          <div><label className="text-xs font-semibold text-muted-foreground">E-mail</label><Input data-testid="user-email" type="email" disabled={!isNew} value={f.Email} onChange={(e) => setF((s) => ({ ...s, Email: e.target.value }))} className="h-9 mt-1" /></div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Site principal</label>
            <Select value={f.Site_Principal_ID || undefined} onValueChange={(v) => setF((s) => ({ ...s, Site_Principal_ID: v }))}>
              <SelectTrigger data-testid="user-site" className="h-9 mt-1"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => <SelectItem key={s.Site_ID || s.id} value={s.Site_ID || s.id}>{s.Nom_Site || s.Nom || s.Site_ID || s.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isNew && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="user-actif" className="accent-primary" checked={f.Actif} onChange={(e) => setF((s) => ({ ...s, Actif: e.target.checked }))} />Compte actif</label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="user-save" disabled={busy} onClick={save}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RolesDialog({ user, roleList, sites, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(user._roles || []));
  const [site, setSite] = useState(user._site || '');
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    if (!site.trim()) { toast.error('Sélectionnez un site'); return; }
    setBusy(true);
    const roles = Array.from(selected).map((rid) => ({ Role_ID: rid, Site_ID: site }));
    const res = await mocApi.setUserRoles(user.Utilisateur_ID || user.Email, roles);
    setBusy(false);
    if (res.ok) { toast.success('Rôles mis à jour'); onSaved(); } else toast.error(res.error || 'Échec');
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-roles-dialog">
        <DialogHeader><DialogTitle className="text-base">Rôles — {user.Nom} {user.Prenom}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Site concerné</label>
            <Select value={site || undefined} onValueChange={setSite}>
              <SelectTrigger data-testid="roles-site" className="h-9 mt-1"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => <SelectItem key={s.Site_ID || s.id} value={s.Site_ID || s.id}>{s.Nom_Site || s.Nom || s.Site_ID || s.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto pr-1">
            {roleList.map(([id, label]) => (
              <label key={id} data-testid={`role-opt-${id}`} className="flex items-center gap-2 text-sm border border-border rounded-sm px-3 py-2 cursor-pointer hover:bg-muted/50">
                <input type="checkbox" className="accent-primary" checked={selected.has(id)} onChange={() => toggle(id)} />
                <span className="font-mono text-xs text-muted-foreground">{id}</span><span>{label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Remplace l'ensemble des rôles de cet utilisateur pour le site choisi.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="roles-save" disabled={busy} onClick={save}>Enregistrer les rôles</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
